import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import { DatePicker } from 'antd'
import Image from 'next/image'
import dayjs from 'dayjs'
import { useTranslation } from 'src/utils/i18n'

const PRESETS = ['All', 'Today', 'Last 7 days', 'Last 14 days', 'Last 30 days', 'Last Year']

// 安卓设备页面统一使用的时间筛选器：预设快捷选项 + 自定义日期区间
// value    —— string[]，形如 ['All'] / ['Today'] / ['Last 7 days'] / ['2024-01-01', '2024-01-31']
// onChange —— (next: string[]) => void，页面拿到后走自己的 buildTimeParams / setDateRange 逻辑
// 弹窗用 portal 渲染到 body，z-index 800 低于 antd 日历弹窗的默认 1000，日历能完整盖在上面
const DateRangeFilter = ({ value = ['All'], onChange, className = '', variant = 'primary' }) => {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, right: 0 })
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const btnRef = useRef(null)
  const panelRef = useRef(null)

  const updatePos = useCallback(() => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setPos({ top: rect.bottom + 6, right: Math.max(8, window.innerWidth - rect.right) })
    }
  }, [])

  // 打开时按当前值回填自定义区间，并且跟随滚动 / 缩放重新定位
  useEffect(() => {
    if (!open) return

    if (Array.isArray(value) && value.length === 2 && value[0] && value[1]) {
      setFrom(value[0])
      setTo(value[1])
      setFromDate(dayjs(value[0]))
      setToDate(dayjs(value[1]))
    } else {
      setFrom('')
      setTo('')
      setFromDate(null)
      setToDate(null)
    }

    updatePos()
    window.addEventListener('resize', updatePos)
    window.addEventListener('scroll', updatePos, true)

    return () => {
      window.removeEventListener('resize', updatePos)
      window.removeEventListener('scroll', updatePos, true)
    }
  }, [open, value, updatePos])

  // 点击按钮和面板以外的地方就收起
  useEffect(() => {
    if (!open) return

    const onMouseDown = e => {
      if (btnRef.current?.contains(e.target)) return
      if (panelRef.current?.contains(e.target)) return

      setOpen(false)
    }

    document.addEventListener('mousedown', onMouseDown)

    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open])

  const apply = () => {
    if (!from || !to) return

    onChange([from, to])
    setOpen(false)
  }

  const isPrimary = variant === 'primary'

  return (
    <>
      <div
        ref={btnRef}
        className={`min-w-[172px] w-auto h-[40px] ${
          isPrimary ? 'bg-[#2A99FF]' : 'bg-[#F8F8F8]'
        } border border-[#E5E7EB] rounded-[8px] flex justify-center items-center cursor-pointer relative px-[10px] whitespace-nowrap ${className}`}
        onClick={e => {
          e.stopPropagation()
          if (!open) updatePos()
          setOpen(v => !v)
        }}
      >
        <Image className='ml-[2px]' src='/images/record-screen/calendar2.svg' width={20} height={20} alt='calendar' />
        <span className={`text-[14px] leading-[16px] ${isPrimary ? 'text-white' : 'text-[#000]'} mx-[10px] whitespace-nowrap`}>
          {Array.isArray(value) && value.length === 1 && value[0]}
          {Array.isArray(value) && value.length === 2 && (
            <>
              {value[0]} ~ {value[1]}
            </>
          )}
        </span>
        <motion.div initial={{ rotate: 0 }} animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <Image src='/images/record-screen/union.svg' width={16} height={16} alt='drop down' />
        </motion.div>
      </div>

      {open && typeof document !== 'undefined'
        ? createPortal(
            <motion.div
              ref={panelRef}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18 }}
              className='fixed z-[800] w-[340px] bg-[#FFFFFF] rounded-[12px] shadow-[0_8px_28px_rgba(0,0,0,0.14)] border border-[#EFF0F2] overflow-hidden'
              style={{ top: pos.top, right: pos.right }}
              onClick={e => e.stopPropagation()}
            >
              {/* 预设快捷筛选 */}
              <div className='px-[16px] pt-[14px] pb-[12px] border-b border-[#F2F3F5]'>
                <div className='text-[13px] font-[600] text-[#1F2329] mb-[10px]'>{t('Quick filter')}</div>
                <div className='flex flex-wrap gap-[8px]'>
                  {PRESETS.map(preset => {
                    const active = Array.isArray(value) && value.length === 1 && value[0] === preset

                    return (
                      <div
                        key={preset}
                        className={`px-[12px] py-[5px] text-[12px] rounded-[8px] cursor-pointer transition-colors ${
                          active
                            ? 'bg-[#2A99FF] text-white'
                            : 'bg-[#F4F6F9] text-[#525866] hover:bg-[#E8F1FF] hover:text-[#2A99FF]'
                        }`}
                        onClick={() => {
                          onChange([preset])
                          setOpen(false)
                        }}
                      >
                        {t(preset)}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* 自定义日期范围 */}
              <div className='px-[16px] py-[14px]'>
                <div className='text-[13px] font-[600] text-[#1F2329] mb-[10px]'>{t('Custom range')}</div>
                <div className='flex items-center gap-[8px]'>
                  <DatePicker
                    className='flex-1'
                    picker='date'
                    format='YYYY-MM-DD'
                    placeholder={t('Start date')}
                    value={fromDate}
                    onChange={(date, dateString) => {
                      setFromDate(date)
                      setFrom(dateString)
                    }}
                    allowClear
                  />
                  <span className='text-[#BBB] text-[14px]'>-</span>
                  <DatePicker
                    className='flex-1'
                    picker='date'
                    format='YYYY-MM-DD'
                    placeholder={t('End date')}
                    value={toDate}
                    onChange={(date, dateString) => {
                      setToDate(date)
                      setTo(dateString)
                    }}
                    allowClear
                  />
                </div>
              </div>

              {/* 底部操作 */}
              <div className='flex border-t border-[#F2F3F5]'>
                <div
                  className='flex-1 py-[11px] text-center text-[14px] text-[#525866] cursor-pointer hover:bg-[#F7F8FA]'
                  onClick={() => setOpen(false)}
                >
                  {t('Cancel')}
                </div>
                <div className='w-[1px] bg-[#F2F3F5]' />
                <div
                  className={`flex-1 py-[11px] text-center text-[14px] ${
                    from && to
                      ? 'text-[#2A99FF] font-[600] cursor-pointer hover:bg-[#F0F7FF]'
                      : 'text-[#A8ABB3] cursor-not-allowed'
                  }`}
                  onClick={() => apply()}
                >
                  {t('apply')}
                </div>
              </div>
            </motion.div>,
            document.body
          )
        : null}
    </>
  )
}

export default DateRangeFilter
